
import { v4 as uuidv4 } from "uuid";
import { 
  BastionFacility, 
  CharacterItem,
  BasicCreatureInstance
} from "@dndModels";
import { Indexable } from "@models";
import { BastionFacilitySize } from "@dndModels/Types";

/**
 * This represents a BastionFacility with the options chosen for a character.
 */
export class CharacterBastionFacility implements Indexable {
  true_id = "";
  _name = "";
  description = "";
  source_id = "";
  source: BastionFacility | null = null;
  subtype = "";
  _size: BastionFacilitySize | null = null;
  items: CharacterItem[] = [];
  creatures: BasicCreatureInstance[] = [];
  customs: { id: string, name: string, count: number, value: number }[] = [];

  get id() {
    return this.true_id;
  }

  get name() {
    if (this._name.length > 0) {
      return this._name;
    }
    let name = this.source_name;
    if (this.subtype.length > 0) {
      name += " " + this.subtype;
    }
    return name;
  }

  get source_name() {
    return this.source?.name??"Nameless";
  }

  get size() {
    if (this._size) {
      return this._size;
    }
    return this.source?.size??"Cramped";
  }

  get capacity() {
    if (this.source) {
      return this.source.capacity_for_size[this.size];
    }
    return 0;
  }

  get special_facility() {
    if (this.source) {
      return this.source.special_facility;
    }
    return false;
  }

  get_profit_percent(char_level: number) {
    if (this.source) {
      const levels = Object.keys(this.source.profit_percent_potence).map(o => +o);
      const potence_level = Math.max(...levels.filter(o => o <= char_level), 0);
      if (this.source.profit_percent_potence[potence_level]) {
        return this.source.profit_percent_potence[potence_level];
      }
    }
    return 0;
  }

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this._name = obj._name ? obj._name : "";
      this.description = obj.description ? obj.description : "";
      this.source_id = obj.source_id ? obj.source_id : "";
      this.subtype = obj.subtype ? obj.subtype : "";
      this._size = obj._size ? obj._size : null;
      this.items = obj.items ? obj.items.map((o: any) => new CharacterItem(o)) : [];
      this.creatures = obj.creatures ? obj.creatures.map((o: any) => new BasicCreatureInstance(o)) : [];
      this.customs = obj.customs ? obj.customs : [];
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      _name: this._name,
      description: this.description,
      source_id: this.source_id,
      subtype: this.subtype,
      _size: this._size,
      items: this.items.map(o => o.toDBObj()),
      creatures: this.creatures.map(o => o.toDBObj()),
      customs: this.customs
    };
  }

  clone(): CharacterBastionFacility {
    const clone = new CharacterBastionFacility();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: CharacterBastionFacility): void {
    this.true_id = copyMe.true_id;
    this._name = copyMe._name;
    this.description = copyMe.description;
    this.source = copyMe.source;
    this.source_id = copyMe.source_id;
    this.subtype = copyMe.subtype;
    this._size = copyMe._size;
    this.items = copyMe.items.map(o => o.clone());
    this.creatures = copyMe.creatures.map(o => o.clone());
    this.customs = copyMe.customs.map(o => { return {...o}; });
  }
}